/**
 * Validation utilities for form inputs
 */

import { validateEmail, validatePhone } from './helpers';

/**
 * Validate business name
 * @param {string} businessName - Business name to validate
 * @returns {{isValid: boolean, error: string|null}}
 */
export const looksLikeEmail = (value) => {
  if (!value || typeof value !== 'string') return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
};

/**
 * Validate business name
 * @param {string} businessName - Business name to validate
 * @param {{ required?: boolean }} options
 * @returns {{isValid: boolean, error: string|null}}
 */
export const validateBusinessName = (businessName, options = {}) => {
  const { required = true } = options;

  if (!businessName || typeof businessName !== 'string' || businessName.trim().length === 0) {
    if (required) {
      return { isValid: false, error: 'Business name is required' };
    }
    return { isValid: true, error: null };
  }
  
  const trimmed = businessName.trim();
  
  if (trimmed.length < 2) {
    return { isValid: false, error: 'Business name must be at least 2 characters' };
  }
  
  // SECURITY: Max length validation to prevent DoS
  if (trimmed.length > 255) {
    return { isValid: false, error: 'Business name must be 255 characters or less' };
  }

  if (looksLikeEmail(trimmed) || trimmed.includes('@')) {
    return { isValid: false, error: 'Business name cannot be an email address' };
  }
  
  return { isValid: true, error: null };
};

/**
 * Validate entity type
 * @param {string} entityType - Entity type to validate
 * @returns {{isValid: boolean, error: string|null}}
 */
/**
 * Validate entity type
 * @param {string} entityType - Entity type to validate
 * @param {{ required?: boolean }} options
 * @returns {{isValid: boolean, error: string|null}}
 */
export const validateEntityType = (entityType, options = {}) => {
  const { required = true } = options;

  if (!entityType || typeof entityType !== 'string' || entityType.trim().length === 0) {
    if (required) {
      return { isValid: false, error: 'Entity type is required' };
    }
    return { isValid: true, error: null };
  }
  
  const trimmed = entityType.trim();
  
  // SECURITY: Max length validation to prevent DoS
  if (trimmed.length > 100) {
    return { isValid: false, error: 'Entity type must be 100 characters or less' };
  }

  if (looksLikeEmail(trimmed) || trimmed.includes('@')) {
    return { isValid: false, error: 'Entity type cannot be an email address' };
  }
  
  return { isValid: true, error: null };
};

/**
 * Validate email (optional field - only validates format if provided)
 * @param {string} email - Email to validate
 * @param {boolean} required - Whether email is required (default: false)
 * @returns {{isValid: boolean, error: string|null}}
 */
export const validateEmailField = (email, required = false) => {
  if (!email || email.trim().length === 0) {
    if (required) {
      return { isValid: false, error: 'Email is required' };
    }
    return { isValid: true, error: null }; // Optional field, empty is OK
  }
  
  if (!validateEmail(email)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }
  
  return { isValid: true, error: null };
};

/**
 * Validate phone number (optional field - only validates format if provided)
 * @param {string} phone - Phone number to validate
 * @param {boolean} required - Whether phone is required (default: false)
 * @returns {{isValid: boolean, error: string|null}}
 */
export const validatePhoneField = (phone, required = false) => {
  if (!phone || phone.trim().length === 0) {
    if (required) {
      return { isValid: false, error: 'Phone number is required' };
    }
    return { isValid: true, error: null }; // Optional field, empty is OK
  }
  
  if (!validatePhone(phone)) {
    return { isValid: false, error: 'Please enter a valid phone number (at least 10 digits)' };
  }
  
  return { isValid: true, error: null };
};

/**
 * Validate entire client profile
 * @param {Object} profile - Profile object with all fields
 * @returns {{isValid: boolean, errors: Object}}
 */
export const validateClientProfile = (profile, options = {}) => {
  const { requireCompany = true } = options;
  const errors = {};
  
  // Required fields (optional when joining platform without a company)
  const businessNameResult = validateBusinessName(profile.businessName, { required: requireCompany });
  if (!businessNameResult.isValid) {
    errors.businessName = businessNameResult.error;
  }
  
  const entityTypeResult = validateEntityType(profile.entityType, { required: requireCompany });
  if (!entityTypeResult.isValid) {
    errors.entityType = entityTypeResult.error;
  }
  
  // Optional fields (only validate format if provided)
  if (profile.ownerEmail) {
    const emailResult = validateEmailField(profile.ownerEmail, false);
    if (!emailResult.isValid) {
      errors.ownerEmail = emailResult.error;
    }
  }
  
  if (profile.businessEmail) {
    const emailResult = validateEmailField(profile.businessEmail, false);
    if (!emailResult.isValid) {
      errors.businessEmail = emailResult.error;
    }
  }
  
  if (profile.ownerPhone) {
    const phoneResult = validatePhoneField(profile.ownerPhone, false);
    if (!phoneResult.isValid) {
      errors.ownerPhone = phoneResult.error;
    }
  }
  
  if (profile.businessPhone) {
    const phoneResult = validatePhoneField(profile.businessPhone, false);
    if (!phoneResult.isValid) {
      errors.businessPhone = phoneResult.error;
    }
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

